import React, { useEffect, useState } from 'react';
import { Tag } from '@gerdau/hefesto-mais-web';
import { Variants } from '@gerdau/hefesto/_shared/types';
import moment from 'moment';
import Table from '../../../../../components/Table';
import {
  EProjectRevisionStatus,
  IFabricationProject
} from '../../../../../store/ducks/fabrication-projects/types';
import { Container } from './styles';
import ViewOptionsButton from './ViewOptionsButton/Facelift';

interface IProjectTableDataSource {
  key: number;
  projectName: string;
  structures: string[];
  creationDate: moment.Moment;
  hasRevisionDateTime: boolean;
  revision: string;
  revisionStatus: string;
  mockupName: string;
  action: IFabricationProject;
}

interface IProjectsTableProps {
  contractNumber: string;
  filteredProjects: IFabricationProject[];
  searchTerm: string;
  updateProjectsTable: () => void;
}

const revisionStatusVariants: {
  [key in EProjectRevisionStatus]: Exclude<Variants, 'secondary' | 'tertiary'>;
} = {
  [EProjectRevisionStatus.CURRENT_REVISION]: 'success',
  [EProjectRevisionStatus.OLD_REVISION]: 'warning',
  [EProjectRevisionStatus.ALL]: 'primary'
};

const ProjectsTable: React.FC<IProjectsTableProps> = ({
  contractNumber,
  filteredProjects,
  searchTerm,
  updateProjectsTable
}) => {
  const [projectData, setProjectData] = useState<IProjectTableDataSource[]>([]);

  useEffect(() => {
    const data: IProjectTableDataSource[] = filteredProjects.map(
      (project, idx) => {
        const creationDate = project.revisionDateTime
          ? moment(project.revisionDateTime, 'YYYY-MM-DD,HH:mm:ss')
          : moment(project.creationDate);
        return {
          key: idx,
          projectName: project.fileName,
          structures: project.structures.map((s) => s.name),
          creationDate,
          hasRevisionDateTime: !!project.revisionDateTime,
          revision: project.revision || ' - ',
          revisionStatus: project.revisionStatus,
          mockupName: project.mockupName || ' - ',
          action: project
        };
      }
    );
    setProjectData(data);
  }, [filteredProjects]);

  const getColumns = () => {
    const getTextWithSearchTerm = (text: string): React.ReactNode => {
      if (text.toUpperCase().includes(searchTerm.toUpperCase())) {
        const startPos = text.toUpperCase().indexOf(searchTerm.toUpperCase());
        return (
          <>
            {text.substring(0, startPos)}
            <strong>
              {text.substring(startPos, startPos + searchTerm.length)}
            </strong>
            {text.substring(
              startPos + searchTerm.length,
              startPos + text.length
            )}
          </>
        );
      }
      return <>{text}</>;
    };

    const columns = [
      {
        width: '20%',
        title: 'Nome do arquivo',
        dataIndex: 'projectName',
        sorter: (a: IProjectTableDataSource, b: IProjectTableDataSource) =>
          a.projectName.localeCompare(b.projectName),
        render: (text: string) => (
          <>{text ? getTextWithSearchTerm(text) : ' - '}</>
        )
      },
      {
        width: '10%',
        title: 'Maquete',
        dataIndex: 'mockupName',
        sorter: (a: IProjectTableDataSource, b: IProjectTableDataSource) =>
          a.mockupName.localeCompare(b.mockupName),
        render: (text: string) => (
          <>{text ? getTextWithSearchTerm(text) : ' - '}</>
        )
      },
      {
        width: '13%',
        title: 'Estrutura',
        dataIndex: 'structures',
        sorter: (a: IProjectTableDataSource, b: IProjectTableDataSource) =>
          a.structures.toString().localeCompare(b.structures.toString()),
        render: (structures: string[]) => (
          <>
            {structures.length > 0
              ? structures.map((structure, idx) => (
                  <p key={idx} className="structure">
                    {getTextWithSearchTerm(structure)}
                  </p>
                ))
              : ' - '}
          </>
        )
      },
      {
        width: '10%',
        title: 'Nº da revisão',
        dataIndex: 'revision',
        sorter: (a: IProjectTableDataSource, b: IProjectTableDataSource) =>
          a.revision.localeCompare(b.revision),
        render: (revision: string) => (
          <>{revision ? getTextWithSearchTerm(revision) : ' - '}</>
        )
      },
      {
        width: '10%',
        title: 'Status da revisão',
        dataIndex: 'revisionStatus',
        sorter: (a: IProjectTableDataSource, b: IProjectTableDataSource) =>
          a.revisionStatus.localeCompare(b.revisionStatus),
        render: (revisionStatus: EProjectRevisionStatus) => (
          <Tag variant={revisionStatusVariants[revisionStatus]}>
            {revisionStatus}
          </Tag>
        )
      },
      {
        width: '10%',
        title: 'Data de atualização',
        dataIndex: 'creationDate',
        sorter: (a: IProjectTableDataSource, b: IProjectTableDataSource) =>
          moment(a.creationDate, 'YYYY-MM-DD').toDate().getTime() -
          moment(b.creationDate, 'YYYY-MM-DD').toDate().getTime(),
        render: (
          creationDate: moment.Moment,
          data: IProjectTableDataSource
        ) => (
          <>
            {getTextWithSearchTerm(
              creationDate.format(
                data.hasRevisionDateTime ? 'DD/MM/YYYY, HH:mm' : 'DD/MM/YYYY'
              )
            )}
          </>
        )
      },
      {
        width: '2%',
        title: '',
        dataIndex: 'action',
        align: 'center' as const,
        render: (project: IFabricationProject) => (
          <>
            <ViewOptionsButton
              contractNumber={contractNumber}
              project={project}
              updateProjectsTable={updateProjectsTable}
            />
          </>
        )
      }
    ];
    return columns;
  };

  return (
    <Container>
      <Table columns={getColumns()} data={projectData} hasPagination />
    </Container>
  );
};

export default ProjectsTable;
